import React from 'react'
import {
  Card,
  Typography,
  Box,
  withStyles,
  CircularProgress,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import { BOOK_A_CALL_URL, trackPlanUpgrade } from '../../../logic'
import styles from './styles'

// Plan card, Tally-style: name and price on one row, a full-width button, then
// a two-column grid of features with an icon, a bold title and a short
// description. Enterprise plans add a use-case eyebrow, "billed yearly", the
// learner allowance, a demo button and customer logos.
const PlanCard = ({ card, onClickHandler, classes, loading }) => {
  const {
    subscription,
    caption,
    price,
    savings,
    isAnnual,
    details,
    action,
    useCase,
    demoUrl,
    nameHighlight,
    learners,
    logos,
    upgradeAction,
  } = card

  return (
    <Card className={classes.root} elevation={0}>
      {/* Brand bar across the top of every plan card. */}
      <Box className={classes.topBar} />
      <Box className={classes.body}>
        {useCase && (
          <Typography className={classes.useCase}>{useCase}</Typography>
        )}
        <Box className={classes.header}>
          <Box>
            <Typography className={classes.name}>
              {nameHighlight && subscription.endsWith(nameHighlight) ? (
                <>
                  {subscription.slice(0, -nameHighlight.length)}
                  <span className={classes.nameHighlight}>{nameHighlight}</span>
                </>
              ) : (
                subscription
              )}
            </Typography>
            {!useCase && (
              <Typography className={classes.caption}>{caption}</Typography>
            )}
          </Box>
          <Box className={classes.priceBlock}>
            <Box className={classes.priceRow}>
              <Typography className={classes.dollarSign}>$</Typography>
              <Typography className={classes.priceValue}>{price}</Typography>
            </Box>
            <Typography className={classes.priceUnit}>
              {useCase && isAnnual ? 'per month, billed yearly' : 'per month'}
            </Typography>
            {learners && (
              <Typography className={classes.learners}>{learners}</Typography>
            )}
            {isAnnual && savings ? (
              <Typography className={classes.savings}>
                Save ${savings.toLocaleString()} with Annual Billing
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* Enterprise: full card width under the price row, so the longer copy
            doesn't squeeze into the left column. */}
        {useCase && (
          <Typography className={classes.captionWide}>{caption}</Typography>
        )}

        {action.isCustom ? (
          <Box className={classes.ctaRow}>
            {upgradeAction && (
              <CoreButton
                fullWidth
                size="large"
                // A downgrade ("Switch to …") is secondary, as on the creator
                // cards; only a real upgrade gets the solid button.
                variant={upgradeAction.active ? 'outlined' : 'contained'}
                color={upgradeAction.active ? 'secondary' : undefined}
                className={
                  upgradeAction.active ? classes.switchButton : classes.button
                }
                disabled={upgradeAction.disabled === true || loading}
                onClick={onClickHandler}
              >
                {loading && upgradeAction.disabled !== true ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  upgradeAction.text
                )}
              </CoreButton>
            )}
            <CoreButton
              fullWidth
              size="large"
              variant="outlined"
              color="primary"
              className={classes.demoButton}
              href={demoUrl || BOOK_A_CALL_URL}
              target="_blank"
              onClick={() => trackPlanUpgrade(subscription)}
            >
              {action.text}
            </CoreButton>
          </Box>
        ) : (
          <CoreButton
            fullWidth
            size="large"
            className={action.active ? classes.switchButton : classes.button}
            variant={action.active ? 'outlined' : 'contained'}
            color={action.active ? 'secondary' : undefined}
            disabled={action.disabled === true || loading}
            onClick={onClickHandler}
          >
            {loading && action.disabled !== true ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              action.text
            )}
          </CoreButton>
        )}

        <Box component="ul" className={classes.grid}>
          {details.map(item => (
            <li key={item.title} className={classes.item}>
              <Box component={Icon} icon={item.icon} className={classes.icon} />
              <Typography className={classes.itemText}>
                <span className={classes.itemTitle}>{item.title}</span>{' '}
                {item.description}
              </Typography>
            </li>
          ))}
        </Box>

        {logos?.length > 0 && (
          <Box className={classes.logosWrap}>
            <Box className={classes.logosBlock}>
              <Typography className={classes.logosLabel}>Trusted by</Typography>
              {/* Four fixed slots so both cards' logo rows line up. */}
              <Box component="ul" className={classes.logosGrid}>
                {logos.map(logo => (
                  <li key={logo.src} className={classes.logoCell}>
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className={classes.logo}
                    />
                  </li>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default withStyles(styles)(PlanCard)
